// PlaybackToolbar — play/pause, step-forward/back, speed, scrub slider.

import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectSimulationStatus,
  selectWalkerPosition,
  selectActiveEventIndex,
  selectActualPath,
  selectAssertionCountsAtStep,
  selectEffectiveAssertionSummary,
  setActiveEventIndex,
  simulationPaused,
} from '../../store/slices/simulationSlice.ts';
import { selectTotalEvents } from '../../store/slices/eventsSlice.ts';
import {
  selectPlaybackSpeed,
  selectNode,
  setPlaybackMode,
  setPlaybackSpeed,
  toggleSidebar,
} from '../../store/slices/uiSlice.ts';
import { walkerStart, walkerStepForward, walkerScrubTo } from '../../store/sagas/walkerSaga.ts';

const SPEEDS = [0.5, 1, 2, 4];

export default function PlaybackToolbar() {
  const dispatch = useDispatch();
  const status = useSelector(selectSimulationStatus);
  const position = useSelector(selectWalkerPosition);
  const activeIdx = useSelector(selectActiveEventIndex);
  const actualPath = useSelector(selectActualPath);
  const totalEvents = useSelector(selectTotalEvents);
  const speed = useSelector(selectPlaybackSpeed);
  const stepCounts = useSelector(selectAssertionCountsAtStep);
  const fullSummary = useSelector(selectEffectiveAssertionSummary);

  const isRunning = status === 'running';
  const isPaused = status === 'paused';
  const isIdle = status === 'idle';
  const isAborted = status === 'failed';
  const isTerminal = status === 'completed' || status === 'failed';

  // Use full summary at completion so the scoreboard matches the verdict banner
  const scoreboard = isTerminal ? fullSummary : stepCounts;

  // When terminal, the user's viewing position is activeEventIndex;
  // during playback, it's walkerPosition.
  const viewPosition = isTerminal ? (activeIdx ?? totalEvents - 1) + 1 : position;

  // Navigate to a step in already-computed results (completed state only)
  const navigateToStep = useCallback(
    (stepIdx: number) => {
      dispatch(setActiveEventIndex(stepIdx));
      const nodeId = actualPath[stepIdx];
      if (nodeId) dispatch(selectNode(nodeId));
    },
    [dispatch, actualPath],
  );

  // Play = auto-advance. Always starts/resumes in auto mode.
  const handlePlayPause = useCallback(() => {
    if (isIdle || isTerminal) {
      // Resume from active frame position (0 if nothing selected)
      const from = isTerminal && activeIdx != null ? activeIdx + 1 : undefined;
      dispatch(setPlaybackMode('auto'));
      dispatch(walkerStart(from));
    } else if (isRunning) {
      dispatch(simulationPaused());
    } else if (isPaused) {
      // Restart from user's current viewing position
      const from = activeIdx != null ? activeIdx + 1 : position;
      dispatch(setPlaybackMode('auto'));
      dispatch(walkerStart(from));
    }
  }, [dispatch, isIdle, isTerminal, isRunning, isPaused, activeIdx, position]);

  // Step forward = single manual step. Works from any state.
  const handleStepForward = useCallback(() => {
    if (isTerminal) {
      // Browse already-computed results — just move activeEventIndex
      const current = activeIdx ?? totalEvents - 1;
      if (current < totalEvents - 1) {
        navigateToStep(current + 1);
      }
      return;
    }
    if (isIdle) {
      // Start fresh in manual mode, then step on next tick
      dispatch(setPlaybackMode('manual'));
      dispatch(walkerStart());
      setTimeout(() => dispatch(walkerStepForward()), 0);
    } else {
      // Running or paused — saga handles pausing auto-play and stepping
      dispatch(walkerStepForward());
    }
  }, [dispatch, isIdle, isTerminal, activeIdx, totalEvents, navigateToStep]);

  // Step back = scrub to previous position.
  const handleStepBack = useCallback(() => {
    if (isTerminal) {
      const current = activeIdx ?? totalEvents - 1;
      if (current > 0) {
        navigateToStep(current - 1);
      }
      return;
    }
    if (position <= 0) return;
    if (isIdle) {
      // Need to start the saga first so it can process the scrub
      dispatch(setPlaybackMode('manual'));
      dispatch(walkerStart());
      setTimeout(() => dispatch(walkerScrubTo(position - 1)), 0);
    } else {
      dispatch(walkerScrubTo(position - 1));
    }
  }, [dispatch, position, isIdle, isTerminal, activeIdx, totalEvents, navigateToStep]);

  // Stop = pause, preserving current position.
  const handleStop = useCallback(() => {
    dispatch(simulationPaused());
  }, [dispatch]);

  const handleScrub = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const pos = parseInt(e.target.value, 10);
      if (isTerminal) {
        // Browse computed results — scrubber value is 0-based step + 1
        navigateToStep(pos > 0 ? pos - 1 : 0);
        return;
      }
      dispatch(walkerScrubTo(pos));
    },
    [dispatch, isTerminal, navigateToStep],
  );

  const handleSpeedChange = useCallback(
    (newSpeed: number) => {
      dispatch(setPlaybackSpeed(newSpeed));
    },
    [dispatch],
  );

  // Replay = restart from the beginning.
  const handleReplay = useCallback(() => {
    dispatch(setPlaybackMode('auto'));
    dispatch(walkerStart(0));
  }, [dispatch]);

  return (
    <div className="playback-toolbar">
      <div className="playback-controls">
        <button
          className="playback-btn"
          onClick={handleStepBack}
          disabled={viewPosition <= 0}
          aria-label="Step back"
        >
          ⏮
        </button>

        <button
          className="playback-btn"
          onClick={handlePlayPause}
          aria-label={isRunning ? 'Pause' : 'Play'}
        >
          {isRunning ? '⏸' : '▶'}
        </button>

        <button className="playback-btn" onClick={handleStepForward} aria-label="Step forward">
          ⏭
        </button>

        <button
          className="playback-btn"
          onClick={handleStop}
          disabled={!isRunning}
          aria-label="Stop"
        >
          ⏹
        </button>

        <button
          className="playback-btn"
          onClick={handleReplay}
          disabled={isIdle}
          aria-label="Replay from start"
          title="Replay from start"
        >
          ↺
        </button>
      </div>

      <div className="playback-scrub">
        <input
          type="range"
          min={0}
          max={totalEvents}
          value={viewPosition}
          onChange={handleScrub}
          aria-label="Scrub position"
        />
        <span className="playback-position">
          {viewPosition} / {totalEvents}
        </span>
      </div>

      {isAborted && (
        <span
          className="playback-aborted"
          role="alert"
          title={`Simulation aborted — ${viewPosition} of ${totalEvents} events processed`}
        >
          ⚠ ABORTED
        </span>
      )}

      {scoreboard.total > 0 && (
        <button
          className="playback-scoreboard"
          onClick={() => dispatch(toggleSidebar())}
          title={
            isAborted
              ? 'Assertion counts up to the abort point — toggle details'
              : 'Toggle assertion details'
          }
        >
          <span className="scoreboard-passed">✓ {scoreboard.passed}</span>
          <span
            className={`scoreboard-failed ${scoreboard.failed > 0 ? 'scoreboard-has-failures' : ''}`}
          >
            ✗ {scoreboard.failed}
          </span>
        </button>
      )}

      <div className="playback-speed">
        {SPEEDS.map((s) => (
          <button
            key={s}
            className={`speed-btn ${speed === s ? 'speed-active' : ''}`}
            onClick={() => handleSpeedChange(s)}
            aria-label={`Speed ${s}x`}
            aria-pressed={speed === s}
          >
            {s}x
          </button>
        ))}
      </div>
    </div>
  );
}
